/**
 * §05 — Targeting system. Ten policies (§5.3), hysteresis (§5.5), prediction (§5.7),
 * Focus-Aim overlay (§5.10).
 *
 * Pure over sim state: consumes NO RNG stream, reads no wall clock, casts no LOS rays (§5.6).
 * Ties break on lowest entity id (§5.9). Acquisition always goes through the spatial hash
 * circle query with a candidate cap — never a full pool scan (§5.4 perf acceptance).
 *
 * This module also owns the per-slot resolved-weapon cache, because §5.11 lists
 * `acquisition_range`, `v_p` and the default policy as §06 inputs to targeting. `weapons.ts`
 * and `projectiles.ts` read the same cache so weapon stats are resolved once per level change,
 * never per tick (§14.3 zero-alloc rule).
 */

import { cfg } from '../core/config';
import {
  evolutionById,
  resolveWeaponStats,
  weaponById,
  type WeaponDef,
  type WeaponStats,
} from '../core/content';
import { nearestX, nearestY, wrapDeltaX, wrapDeltaY, wrapDist2 } from './wrap';
import { POOL_CAPACITY } from './world';
import type { Enemy } from './entities';
import type { World } from './world';

// --- policy codes (string → stable int, so hot loops never compare strings) -----------------

export const POLICY_NEAREST = 1;
export const POLICY_NEAREST_THREAT = 2;
export const POLICY_LOWEST_HP = 3;
export const POLICY_HIGHEST_HP = 4;
export const POLICY_DENSEST_CLUSTER = 5;
export const POLICY_FORWARD_CONE = 6;
export const POLICY_LAST_TARGET = 7;
export const POLICY_BOSS_PRIORITY = 8;
export const POLICY_ELITE_PRIORITY = 9;
export const POLICY_PLAYER_SELECTED = 10;

export const POLICY_CODES: Readonly<Record<string, number>> = {
  nearest: POLICY_NEAREST,
  'nearest-threat': POLICY_NEAREST_THREAT,
  'lowest-hp': POLICY_LOWEST_HP,
  'highest-hp': POLICY_HIGHEST_HP,
  'densest-cluster': POLICY_DENSEST_CLUSTER,
  'forward-cone': POLICY_FORWARD_CONE,
  'last-target': POLICY_LAST_TARGET,
  'boss-priority': POLICY_BOSS_PRIORITY,
  'elite-priority': POLICY_ELITE_PRIORITY,
  'player-selected': POLICY_PLAYER_SELECTED,
};

export const POLICY_NAMES: readonly string[] = [
  'nearest',
  'nearest-threat',
  'lowest-hp',
  'highest-hp',
  'densest-cluster',
  'forward-cone',
  'last-target',
  'boss-priority',
  'elite-priority',
  'player-selected',
];

/** §5.9 — score comparison epsilon; |Δ| ≤ ε is a tie and resolves on lowest entity id. */
export const SCORE_EPSILON = 1e-6;
/** §5.8 — global acquisition-range clamp (readability §11, perf §14). */
export const MAX_ACQUISITION_RANGE = 60;
/**
 * §5.8 — the on-screen acquisition clamp, config path `targeting.visible_range_u`.
 *
 * A weapon may declare 62 u of reach, but the camera frames 53.3 × 30 u at the reference
 * aspect, so acquisition at the weapon's own range fires at bodies the player cannot see:
 * shots leave the muzzle toward nothing and kills land off-frame. Every acquisition path
 * therefore reaches no further than `min(weaponRange, visible)`.
 *
 * A CIRCLE, not the view rectangle, and the reason is determinism rather than taste. The
 * framing is a function of the viewport aspect (`BASE_VIEW_HEIGHT` widened to the aspect,
 * floored at 20 u and capped at 68 × 42), so a rectangle clamp would make what a weapon
 * can shoot a function of the player's window shape — the same seed would play differently
 * in portrait and in ultrawide, and no replay would reproduce. A fixed radius is the same
 * number on every device, costs one wrapped distance compare per candidate, and the render
 * layer can draw it as one ring.
 *
 * 30 u is the half-diagonal of the reference 53.3 × 30 framing (30.6 u), rounded down.
 * Every body inside the reference frame — corners included — is targetable, which is the
 * margin the "targetable the moment it enters frame" rule asks for; horizontally the ring
 * sits 3.3 u beyond the frame edge. It is not exact: the ring still reaches ~15 u above and
 * below a 16:9 frame, because a circle that covers a 53 u width cannot stop at a 30 u
 * height. Bounded overshoot in one axis is the price of a bearing-independent radius.
 */
export const VISIBLE_RANGE_KEY = 'targeting.visible_range_u';
export const DEFAULT_VISIBLE_RANGE = 18;
/** §5.8 — default acquisition range when a weapon declares none. */
export const DEFAULT_ACQUISITION_RANGE = 20;
/** §5.5 — Focus-Aim overlay uses a tighter hysteresis margin and is exempt from min-hold. */
export const POLICY10_HYSTERESIS_MARGIN = 0.1;
/** §5.5 — voluntary switches wait 250 ms (15 ticks @60 Hz). */
export const MIN_HOLD_TICKS = 15;
/** §5.4 — staggered reacquisition: slot s evaluates on (tick + s) mod 6. */
export const REACQUIRE_TICKS = 6;
/** §5.4 — focus weapon while Focus Aim is held reacquires every 50 ms. */
export const FOCUS_REACQUIRE_TICKS = 3;
/** §5.2 fC — cluster factor sample radius and normalizer. */
export const CLUSTER_RADIUS = 8;
export const CLUSTER_NORMALIZER = 12;
/** §5.3 policy 6 — prefilter half-angle, and the fF falloff angle from §5.2. */
export const FORWARD_PREFILTER_COS = Math.cos((60 * Math.PI) / 180);
export const FORWARD_FALLOFF_DEG = 45;
/** §5.10 — Focus-Aim cone half-angle. */
export const FOCUS_CONE_DEG = 30;
/** §5.8 — leash: the current target is retained until d > 1.1·R. */
export const LEASH_FACTOR = 1.1;
/** §5.3 policy 7 — last-target candidates are limited to d ≤ 1.2·R. */
export const LAST_TARGET_RANGE_FACTOR = 1.2;
/** §5.7 — prediction lead is clamped to 0.5 s. */
export const MAX_LEAD_S = 0.5;

// --- module-scope scratch (allocated once; §14.3) --------------------------------------------

const MAX_CANDIDATES = 256;
const candBuf = new Int32Array(1024);
const candIdx = new Int32Array(MAX_CANDIDATES);
const candDist = new Float64Array(MAX_CANDIDATES);
const clusterBuf = new Int32Array(1024);
const clusterStamp = new Int32Array(POOL_CAPACITY.enemies);
let clusterStampCounter = 1;

/** Entity-id → pool-index open-addressed map, rebuilt once per tick. */
const ID_MAP_BITS = 11;
const ID_MAP_SIZE = 1 << ID_MAP_BITS; // 2048 > 2 × enemy capacity
const ID_MAP_MASK = ID_MAP_SIZE - 1;
const idKeys = new Int32Array(ID_MAP_SIZE);
const idVals = new Int32Array(ID_MAP_SIZE);
let idMapTick = -1;
let idMapWorld: World | null = null;

/** §5.3 policy 6 — last non-zero heading, held while |v| < 0.5 u/s. */
let lastHeadingX = 1;
let lastHeadingY = 0;

/** §5.5 — per-slot min-hold bookkeeping (one player, ≤ 4 weapon slots). */
const slotTargetSince = new Int32Array(4);
const slotNextEval = new Int32Array(4);
/** §5.10 — Focus-Aim held state per slot, so the release edge forces a re-evaluation. */
const slotFocusHeld = new Int32Array(4);

/** §5.7 output — reused every call, never allocated. */
export const aimOut = { x: 0, y: 0 };

// --- entity-id index -------------------------------------------------------------------------

function idHash(id: number): number {
  return (Math.imul(id, 2654435761) >>> (32 - ID_MAP_BITS)) & ID_MAP_MASK;
}

/**
 * Rebuild the entity-id → pool-index map. O(live enemies), called at most once per tick per
 * system entry point; repeated calls in the same tick are no-ops.
 */
export function rebuildEnemyIndex(w: World, force = false): void {
  if (!force && idMapWorld === w && idMapTick === w.tick) return;
  idMapWorld = w;
  idMapTick = w.tick;
  idKeys.fill(0);
  const list = w.enemies.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (e.id === 0) continue;
    let h = idHash(e.id);
    for (let probe = 0; probe < ID_MAP_SIZE; probe++) {
      if (idKeys[h] === 0) {
        idKeys[h] = e.id;
        idVals[h] = e.pool_index;
        break;
      }
      if (idKeys[h] === e.id) {
        idVals[h] = e.pool_index;
        break;
      }
      h = (h + 1) & ID_MAP_MASK;
    }
  }
}

/** Resolve an entity id to a live enemy, or null. Never allocates. */
export function enemyByEntityId(w: World, id: number): Enemy | null {
  if (id === 0) return null;
  rebuildEnemyIndex(w);
  let h = idHash(id);
  for (let probe = 0; probe < ID_MAP_SIZE; probe++) {
    const k = idKeys[h];
    if (k === 0) return null;
    if (k === id) {
      const e = w.enemies.slots[idVals[h]];
      return e.alive && e.id === id ? e : null;
    }
    h = (h + 1) & ID_MAP_MASK;
  }
  return null;
}

/** §5.1 candidate filter — alive, materialized, targetable, not already dying. */
export function isTargetable(e: Enemy): boolean {
  return e.alive && e.targetable && e.hp > 0 && e.death_anim === 0;
}

// --- resolved weapon runtime ------------------------------------------------------------------

/** Behaviour ids are re-declared here to avoid a targeting → projectiles import cycle. */
const B_LINEAR = 0;
const B_ORBITAL = 2;
const B_BEAM = 3;
const B_LOBBED = 4;
const B_ZONE = 5;
const B_CHAIN = 6;
const B_MINION = 7;
const B_AURA = 8;
const B_MELEE = 9;

/** §14.2 — stable visual-class integer per weapon; R6 maps these to InstancedMeshes. */
export const VISUAL_CLASS_BY_WEAPON: Readonly<Record<string, number>> = {
  'weapon.spear-jelly-lance': 0,
  'weapon.krill-scatter': 1,
  'weapon.barreleye-gaze': 2,
  'weapon.copepod-halo': 3,
  'weapon.atolla-nova': 4,
  'weapon.angler-lantern-mine': 5,
  'weapon.siphonophore-chain-whip': 6,
  'weapon.bobtail-scion': 7,
  'weapon.brine-pool': 8,
  'weapon.vampire-shroud': 9,
  'weapon.gulper-maw': 10,
  'weapon.loosejaw-searchlight': 11,
};

const BEHAVIOUR_BY_WEAPON: Readonly<Record<string, number>> = {
  'weapon.spear-jelly-lance': B_LINEAR,
  'weapon.krill-scatter': B_LINEAR,
  'weapon.barreleye-gaze': B_BEAM,
  'weapon.copepod-halo': B_ORBITAL,
  'weapon.atolla-nova': B_AURA,
  'weapon.angler-lantern-mine': B_LOBBED,
  'weapon.siphonophore-chain-whip': B_CHAIN,
  'weapon.bobtail-scion': B_MINION,
  'weapon.brine-pool': B_ZONE,
  'weapon.vampire-shroud': B_AURA,
  'weapon.gulper-maw': B_MELEE,
  'weapon.loosejaw-searchlight': B_BEAM,
};

/**
 * Flat numeric view of a weapon at its current level (+ evolution). Rebuilt only when
 * `def_id` / `level` / `evolved` change, i.e. on level-up — never per tick.
 */
export interface WeaponRuntime {
  def_id: string;
  level: number;
  evolved: boolean;
  valid: boolean;
  policy: string;
  policy_code: number;
  behaviour: number;
  visual_class: number;

  damage: number;
  rate: number;
  speed: number;
  count: number;
  penetration: number;
  ricochet: number;
  duration_s: number;
  range: number;
  radius: number;
  trigger_radius: number;
  width: number;
  length: number;
  spread_deg: number;
  knockback: number;
  chain_targets: number;
  chain_falloff: number;
  arm_time_s: number;
  angular_speed: number;
  hit_cooldown_s: number;
  revolution_s: number;
  contact_tick_s: number;
  contact_dps: number;
  slow_pct: number;
  cast_cooldown_s: number;
  respawn_s: number;
  cone_deg: number;
  lunge_u: number;
  iframes_s: number;
  cooldown_s: number;
  absorb: number;
  secondary_pct: number;

  // --- evolution parameters (0 when not evolved) ---
  evo_barbs: number;
  evo_barb_damage_pct: number;
  evo_barb_range: number;
  evo_arms: number;
  evo_arm_rotation: number;
  evo_arm_rate_mult: number;
  evo_ricochet_pct: number;
  evo_burn_pct: number;
  evo_burn_duration_s: number;
  evo_burn_stacks: number;
  evo_ramp_pct_per_s: number;
  evo_ramp_cap_pct: number;
  evo_flare_damage: number;
  evo_flare_interval_s: number;
  evo_rings: number;
  evo_ring_mult: Float64Array;
  evo_mark_pct: number;
  evo_mark_duration_s: number;
  evo_micro_mines: number;
  evo_micro_damage_pct: number;
  evo_micro_radius: number;
  evo_taunt_radius: number;
  evo_taunt_duration_s: number;
  evo_node_duration_s: number;
  evo_node_retargets: number;
  evo_node_damage_pct: number;
  evo_fork_branches: number;
  evo_fork_damage_pct: number;
  evo_volley_interval_s: number;
  evo_volley_damage_pct: number;
  evo_crit_inherit_pct: number;
  evo_merge_bonus_pct: number;
  evo_merge_cap_mult: number;
  evo_amp_pct: number;
  evo_player_regen: number;
  evo_stored_cap: number;
  evo_deton_mult: number;
  evo_deton_trigger: number;
  evo_deton_after_s: number;
  evo_move_speed_pct: number;
  evo_devour_hp_pct: number;
  evo_devour_heal: number;
  evo_devour_heal_cap: number;
  evo_cdr_per_devour_s: number;
  evo_cd_floor_s: number;
  evo_eclipse_pct: number;
  evo_eclipse_duration_s: number;
  evo_focus_bonus_pct: number;
  evo_contact_dps_per_arm: number;
}

function createRuntime(): WeaponRuntime {
  return {
    def_id: '',
    level: 0,
    evolved: false,
    valid: false,
    policy: 'nearest',
    policy_code: POLICY_NEAREST,
    behaviour: B_LINEAR,
    visual_class: 0,
    damage: 0,
    rate: 0,
    speed: 0,
    count: 1,
    penetration: 0,
    ricochet: 0,
    duration_s: 0,
    range: DEFAULT_ACQUISITION_RANGE,
    radius: 0,
    trigger_radius: 0,
    width: 0,
    length: 0,
    spread_deg: 0,
    knockback: 0,
    chain_targets: 0,
    chain_falloff: 0,
    arm_time_s: 0,
    angular_speed: 0,
    hit_cooldown_s: 0,
    revolution_s: 0,
    contact_tick_s: 0,
    contact_dps: 0,
    slow_pct: 0,
    cast_cooldown_s: 0,
    respawn_s: 0,
    cone_deg: 0,
    lunge_u: 0,
    iframes_s: 0,
    cooldown_s: 0,
    absorb: 0,
    secondary_pct: 0,
    evo_barbs: 0,
    evo_barb_damage_pct: 0,
    evo_barb_range: 0,
    evo_arms: 0,
    evo_arm_rotation: 0,
    evo_arm_rate_mult: 1,
    evo_ricochet_pct: 0,
    evo_burn_pct: 0,
    evo_burn_duration_s: 0,
    evo_burn_stacks: 0,
    evo_ramp_pct_per_s: 0,
    evo_ramp_cap_pct: 0,
    evo_flare_damage: 0,
    evo_flare_interval_s: 0,
    evo_rings: 0,
    evo_ring_mult: new Float64Array(4),
    evo_mark_pct: 0,
    evo_mark_duration_s: 0,
    evo_micro_mines: 0,
    evo_micro_damage_pct: 0,
    evo_micro_radius: 0,
    evo_taunt_radius: 0,
    evo_taunt_duration_s: 0,
    evo_node_duration_s: 0,
    evo_node_retargets: 0,
    evo_node_damage_pct: 0,
    evo_fork_branches: 0,
    evo_fork_damage_pct: 0,
    evo_volley_interval_s: 0,
    evo_volley_damage_pct: 0,
    evo_crit_inherit_pct: 0,
    evo_merge_bonus_pct: 0,
    evo_merge_cap_mult: 1,
    evo_amp_pct: 0,
    evo_player_regen: 0,
    evo_stored_cap: 0,
    evo_deton_mult: 0,
    evo_deton_trigger: 0,
    evo_deton_after_s: 0,
    evo_move_speed_pct: 0,
    evo_devour_hp_pct: 0,
    evo_devour_heal: 0,
    evo_devour_heal_cap: 0,
    evo_cdr_per_devour_s: 0,
    evo_cd_floor_s: 0,
    evo_eclipse_pct: 0,
    evo_eclipse_duration_s: 0,
    evo_focus_bonus_pct: 0,
    evo_contact_dps_per_arm: 0,
  };
}

const runtimes: WeaponRuntime[] = [createRuntime(), createRuntime(), createRuntime(), createRuntime()];
/** Extra runtime used by the active slot (weapon.gulper-maw). */
const activeRuntime = createRuntime();

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function areaNum(stats: WeaponStats, key: string, fallback: number): number {
  const a = stats.area as Record<string, unknown> | undefined;
  if (!a) return fallback;
  return num(a[key], fallback);
}

/** §5.8 — acquisition range, derived from the §06 entry and clamped to 60 u. */
function acquisitionRange(stats: WeaponStats): number {
  let r = num(stats.range_u, -1);
  if (r < 0) r = areaNum(stats, 'range_u', -1);
  if (r < 0) r = areaNum(stats, 'cast_range', -1);
  if (r < 0) r = areaNum(stats, 'beam_length', -1);
  if (r < 0) r = DEFAULT_ACQUISITION_RANGE;
  return Math.min(r, MAX_ACQUISITION_RANGE);
}

function applyEvolution(rt: WeaponRuntime, def: WeaponDef): void {
  const evoId = typeof def.evolution_id === 'string' ? def.evolution_id : '';
  if (evoId === '') return;
  const evo = evolutionById.get(evoId);
  if (!evo) return;
  const transformed = evo.transformed as { stats?: Record<string, unknown> } | undefined;
  const s = transformed?.stats;
  if (!s) return;

  // shared stat overrides
  rt.damage = num(s.damage, rt.damage);
  rt.rate = num(s.rate_per_s, rt.rate);
  rt.count = num(s.count, rt.count);
  if (typeof s.penetration === 'number') rt.penetration = s.penetration;
  if (typeof s.orbit_radius === 'number') rt.radius = s.orbit_radius;
  if (typeof s.beam_width === 'number') rt.width = s.beam_width;
  if (typeof s.knockback_u === 'number') rt.knockback = s.knockback_u;
  if (typeof s.lunge_u === 'number') rt.lunge_u = s.lunge_u;
  if (typeof s.contact_tick_s === 'number') rt.contact_tick_s = s.contact_tick_s;

  rt.evo_barbs = num(s.barb_count, evoId === 'evolution.kingspear-medusa' ? 3 : 0);
  rt.evo_barb_damage_pct = evoId === 'evolution.kingspear-medusa' ? 40 : 0;
  rt.evo_barb_range = evoId === 'evolution.kingspear-medusa' ? 12 : 0;

  rt.evo_arms = num(s.emitter_arms, num(s.arms, 0));
  rt.evo_arm_rotation = num(s.arm_rotation_deg_per_s, 0);
  rt.evo_arm_rate_mult = num(s.arm_rate_multiplier, 1);
  rt.evo_ricochet_pct = num(s.ricochet_damage_pct, 0);
  if (rt.evo_ricochet_pct > 0) rt.ricochet = Math.max(1, rt.ricochet);

  rt.secondary_pct = num(s.secondary_damage_pct, rt.secondary_pct);
  if (typeof s.secondary_targets === 'number') rt.penetration = 1 + s.secondary_targets;
  rt.evo_burn_pct = num(s.burn_pct_of_tick, 0);
  rt.evo_burn_duration_s = num(s.burn_duration_s, 0);
  rt.evo_burn_stacks = num(s.burn_stacks, 0);
  rt.evo_ramp_pct_per_s = num(s.ramp_pct_per_s, 0);
  rt.evo_ramp_cap_pct = num(s.ramp_cap_pct, 0);

  rt.evo_flare_damage = num(s.flare_damage, 0);
  rt.evo_flare_interval_s = num(s.flare_interval_s, 0);

  rt.evo_rings = num(s.rings, 0);
  const mults = s.ring_damage_multipliers;
  if (Array.isArray(mults)) {
    for (let i = 0; i < rt.evo_ring_mult.length; i++) {
      rt.evo_ring_mult[i] = i < mults.length ? num(mults[i], 0) : 0;
    }
  }
  rt.evo_mark_pct = num(s.mark_damage_taken_pct, 0);
  rt.evo_mark_duration_s = num(s.mark_duration_s, 0);

  if (typeof s.lob_range_u === 'number' && s.lob_range_u < 0) rt.range = MAX_ACQUISITION_RANGE;
  rt.evo_micro_mines = num(s.micro_mines_per_blast, 0);
  rt.evo_micro_damage_pct = num(s.micro_mine_damage_pct, 0);
  rt.evo_micro_radius = num(s.micro_mine_radius, 0);
  rt.evo_taunt_radius = num(s.taunt_radius, 0);
  rt.evo_taunt_duration_s = num(s.taunt_duration_s, 0);

  rt.evo_node_duration_s = num(s.node_duration_s, 0);
  rt.evo_node_retargets = num(s.node_retargets, 0);
  rt.evo_node_damage_pct = num(s.node_damage_pct, 0);
  rt.evo_fork_branches = num(s.fork_branches, 0);
  rt.evo_fork_damage_pct = num(s.fork_damage_pct, 0);

  rt.evo_volley_interval_s = num(s.volley_interval_s, 0);
  rt.evo_volley_damage_pct = num(s.volley_damage_pct, 0);
  rt.evo_crit_inherit_pct = num(s.crit_chance_inherit_pct, 0);

  rt.evo_merge_bonus_pct = num(s.merge_radius_bonus_pct, 0);
  rt.evo_merge_cap_mult = num(s.merge_radius_cap_multiplier, 1);
  rt.evo_amp_pct = num(s.damage_taken_amp_pct, 0);
  rt.evo_player_regen = num(s.player_regen_hp_per_s, 0);

  rt.evo_stored_cap = num(s.stored_damage_cap, 0);
  rt.evo_deton_mult = num(s.detonation_multiplier, 0);
  rt.evo_deton_trigger = num(s.detonation_trigger_stored, 0);
  rt.evo_deton_after_s = num(s.detonation_trigger_s, 0);
  rt.evo_move_speed_pct = num(s.move_speed_pct_while_intact, 0);

  rt.evo_devour_hp_pct = num(s.devour_hp_threshold_pct, 0);
  rt.evo_devour_heal = num(s.devour_heal_hp, 0);
  rt.evo_devour_heal_cap = num(s.devour_heal_cap_per_cast, 0);
  rt.evo_cdr_per_devour_s = num(s.cooldown_reduction_per_devour_s, 0);
  rt.evo_cd_floor_s = num(s.cooldown_floor_s, 0);

  rt.evo_eclipse_pct = num(s.eclipse_damage_taken_pct, 0);
  rt.evo_eclipse_duration_s = num(s.eclipse_duration_s, 0);
  rt.evo_focus_bonus_pct = num(s.focus_cone_tick_bonus_pct, 0);
  rt.evo_contact_dps_per_arm = num(s.contact_dps_per_arm, 0);
  if (rt.evo_contact_dps_per_arm > 0) rt.contact_dps = rt.evo_contact_dps_per_arm;
}

function fillRuntime(rt: WeaponRuntime, defId: string, level: number, evolved: boolean): void {
  rt.def_id = defId;
  rt.level = level;
  rt.evolved = evolved;
  const def = weaponById.get(defId);
  if (!def || level <= 0) {
    rt.valid = false;
    return;
  }
  const stats = resolveWeaponStats(def, level);
  rt.valid = true;
  rt.policy = def.targeting_policy;
  rt.policy_code = POLICY_CODES[def.targeting_policy] ?? POLICY_NEAREST;
  rt.behaviour = BEHAVIOUR_BY_WEAPON[defId] ?? B_LINEAR;
  rt.visual_class = VISUAL_CLASS_BY_WEAPON[defId] ?? 0;

  rt.damage = num(stats.damage, 0);
  rt.rate = num(stats.rate_per_s, 1);
  rt.speed = num(stats.speed_u_per_s, 0);
  rt.count = num(stats.count, 1);
  rt.penetration = num(stats.penetration, 0);
  rt.ricochet = num(stats.ricochet, 0);
  rt.duration_s = num(stats.duration_s, 0);
  rt.range = acquisitionRange(stats);
  rt.radius = areaNum(stats, 'radius', areaNum(stats, 'blast_radius', areaNum(stats, 'pulse_radius', areaNum(stats, 'orbit_radius', areaNum(stats, 'impact_radius', 0)))));
  rt.trigger_radius = areaNum(stats, 'trigger_radius', 0);
  rt.width = areaNum(stats, 'beam_width', 0);
  rt.length = areaNum(stats, 'beam_length', 0);
  rt.spread_deg = areaNum(stats, 'spread_cone_deg', 0);
  rt.knockback = num(stats.knockback_u, 0);
  rt.chain_targets = num(stats.chain_targets, 0);
  rt.chain_falloff = num(stats.chain_falloff_pct, 0);
  rt.arm_time_s = num(stats.arm_time_s, 0);
  rt.angular_speed = num(stats.angular_speed_deg_per_s, 0);
  rt.hit_cooldown_s = num(stats.per_enemy_hit_cooldown_s, 0);
  rt.revolution_s = num(stats.revolution_s, 0);
  rt.contact_tick_s = num(stats.contact_tick_s, 0);
  rt.contact_dps = num(stats.contact_dps, 0);
  rt.slow_pct = num(stats.slow_pct, 0);
  rt.cast_cooldown_s = num(stats.cast_cooldown_s, num(stats.deploy_cooldown_s, 0));
  rt.respawn_s = num(stats.respawn_s, 0);
  rt.cone_deg = areaNum(stats, 'cone_deg', 0);
  rt.lunge_u = areaNum(stats, 'lunge_u', 0);
  rt.iframes_s = num(stats.iframes_s, 0);
  rt.cooldown_s = num(stats.cooldown_s, 0);
  rt.absorb = num(stats.absorb_flat, 0);
  rt.secondary_pct = num(stats.secondary_target_damage_pct, 0);

  // reset evolution block before (re)applying
  resetEvoDefaults(rt);
  if (evolved) applyEvolution(rt, def);
}

function resetEvoDefaults(rt: WeaponRuntime): void {
  rt.evo_barbs = 0;
  rt.evo_barb_damage_pct = 0;
  rt.evo_barb_range = 0;
  rt.evo_arms = 0;
  rt.evo_arm_rotation = 0;
  rt.evo_arm_rate_mult = 1;
  rt.evo_ricochet_pct = 0;
  rt.evo_burn_pct = 0;
  rt.evo_burn_duration_s = 0;
  rt.evo_burn_stacks = 0;
  rt.evo_ramp_pct_per_s = 0;
  rt.evo_ramp_cap_pct = 0;
  rt.evo_flare_damage = 0;
  rt.evo_flare_interval_s = 0;
  rt.evo_rings = 0;
  rt.evo_ring_mult.fill(0);
  rt.evo_mark_pct = 0;
  rt.evo_mark_duration_s = 0;
  rt.evo_micro_mines = 0;
  rt.evo_micro_damage_pct = 0;
  rt.evo_micro_radius = 0;
  rt.evo_taunt_radius = 0;
  rt.evo_taunt_duration_s = 0;
  rt.evo_node_duration_s = 0;
  rt.evo_node_retargets = 0;
  rt.evo_node_damage_pct = 0;
  rt.evo_fork_branches = 0;
  rt.evo_fork_damage_pct = 0;
  rt.evo_volley_interval_s = 0;
  rt.evo_volley_damage_pct = 0;
  rt.evo_crit_inherit_pct = 0;
  rt.evo_merge_bonus_pct = 0;
  rt.evo_merge_cap_mult = 1;
  rt.evo_amp_pct = 0;
  rt.evo_player_regen = 0;
  rt.evo_stored_cap = 0;
  rt.evo_deton_mult = 0;
  rt.evo_deton_trigger = 0;
  rt.evo_deton_after_s = 0;
  rt.evo_move_speed_pct = 0;
  rt.evo_devour_hp_pct = 0;
  rt.evo_devour_heal = 0;
  rt.evo_devour_heal_cap = 0;
  rt.evo_cdr_per_devour_s = 0;
  rt.evo_cd_floor_s = 0;
  rt.evo_eclipse_pct = 0;
  rt.evo_eclipse_duration_s = 0;
  rt.evo_focus_bonus_pct = 0;
  rt.evo_contact_dps_per_arm = 0;
}

/**
 * Resolved runtime for weapon slot `slotIndex` (0..3). Recomputed only when the slot's
 * def/level/evolution changed. Returns `valid === false` for an empty slot.
 */
export function weaponRuntime(w: World, slotIndex: number): WeaponRuntime {
  const slot = w.player.weapons[slotIndex];
  const rt = runtimes[slotIndex];
  if (rt.def_id !== slot.def_id || rt.level !== slot.level || rt.evolved !== slot.evolved) {
    fillRuntime(rt, slot.def_id, slot.level, slot.evolved);
  }
  return rt;
}

/** Resolved runtime for the active slot (weapon.gulper-maw). */
export function activeWeaponRuntime(defId: string, level: number, evolved: boolean): WeaponRuntime {
  if (activeRuntime.def_id !== defId || activeRuntime.level !== level || activeRuntime.evolved !== evolved) {
    fillRuntime(activeRuntime, defId, level, evolved);
  }
  return activeRuntime;
}

/** §5.10 — effective policy for a slot: non-focus slots are locked to the §06 default. */
export function effectivePolicy(w: World, slotIndex: number, rt: WeaponRuntime): number {
  if (slotIndex !== w.player.focus_slot) return rt.policy_code;
  const override = POLICY_CODES[w.player.weapons[slotIndex].policy];
  if (override === undefined || override === POLICY_PLAYER_SELECTED) return rt.policy_code;
  return override;
}

// --- candidate gathering ------------------------------------------------------------------------

/**
 * The clamp radius in world units, read live so it stays tunable and so the render layer
 * can draw the ring from the same registry entry.
 */
export function visibleAcquisitionRange(): number {
  const v = cfg.get(VISIBLE_RANGE_KEY);
  return v > 0 ? v : DEFAULT_VISIBLE_RANGE;
}

/** `min(weaponRange, visible)` — the reach any acquisition path may actually use. */
export function clampToVisible(range: number): number {
  const v = visibleAcquisitionRange();
  return range < v ? range : v;
}

/**
 * True when a point is inside the clamp ring around the ship, measured the short way
 * round (AMD-24): a body three units through the seam is on screen and stays targetable.
 *
 * Chain links and ricochets in `projectiles.ts` call this directly, because a hop that
 * starts on a visible enemy and lands on an off-screen one is the same bug as acquiring
 * one outright. It never touches a projectile already in flight — only the choice of a
 * NEW target goes through here.
 */
export function isWithinVisibleRange(w: World, x: number, y: number): boolean {
  const v = visibleAcquisitionRange();
  return wrapDist2(w.player.pos.x, w.player.pos.y, x, y) <= v * v;
}

let candCount = 0;
let candMaxHp = 1;

function gatherCandidates(w: World, ox: number, oy: number, radius: number): void {
  candCount = 0;
  candMaxHp = 1;
  const cap = Math.min(MAX_CANDIDATES, cfg.get('targeting.candidate_cap') | 0 || 64);
  const n = w.hash.queryCircle(ox, oy, radius, candBuf, candBuf.length);
  const stamp = w.nextStamp();
  const r2 = radius * radius;
  const vis = visibleAcquisitionRange();
  const vis2 = vis * vis;
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  for (let i = 0; i < n; i++) {
    const pi = candBuf[i];
    if (w.seenStamp[pi] === stamp) continue;
    w.seenStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - ox);
    const dy = wrapDeltaY(e.pos.y - oy);
    const d2 = dx * dx + dy * dy;
    if (d2 > r2) continue;
    // §5.8 — visibility is measured from the ship, never from the firing origin: a scion
    // or a ricochet impact point may sit near the rim, and its next target still has to be
    // one the player can see.
    if (wrapDist2(px, py, e.pos.x, e.pos.y) > vis2) continue;
    if (candCount >= cap) break;
    candIdx[candCount] = pi;
    candDist[candCount] = Math.sqrt(d2);
    candCount++;
    if (e.max_hp > candMaxHp) candMaxHp = e.max_hp;
  }
}

/** §5.2 fC — enemies within 8 u of the candidate, normalized by 12 and clamped to 1. */
function clusterFactor(w: World, x: number, y: number): number {
  const n = w.hash.queryCircle(x, y, CLUSTER_RADIUS, clusterBuf, clusterBuf.length);
  const stamp = clusterStampCounter++;
  if (clusterStampCounter > 0x7ffffff0) {
    clusterStamp.fill(0);
    clusterStampCounter = 1;
  }
  const r2 = CLUSTER_RADIUS * CLUSTER_RADIUS;
  let count = 0;
  for (let i = 0; i < n; i++) {
    const pi = clusterBuf[i];
    if (pi < 0 || pi >= clusterStamp.length) continue;
    if (clusterStamp[pi] === stamp) continue;
    clusterStamp[pi] = stamp;
    const e = w.enemies.slots[pi];
    if (!isTargetable(e)) continue;
    const dx = wrapDeltaX(e.pos.x - x);
    const dy = wrapDeltaY(e.pos.y - y);
    if (dx * dx + dy * dy > r2) continue;
    count++;
  }
  const f = count / CLUSTER_NORMALIZER;
  return f > 1 ? 1 : f;
}

/** §5.3 policy 6 — reference heading: velocity direction, else the last non-zero heading. */
function refreshHeading(w: World): void {
  const vx = w.player.vel.x;
  const vy = w.player.vel.y;
  const sp = Math.sqrt(vx * vx + vy * vy);
  if (sp >= 0.5) {
    lastHeadingX = vx / sp;
    lastHeadingY = vy / sp;
  }
}

function scoreCandidate(
  w: World,
  e: Enemy,
  d: number,
  range: number,
  policy: number,
  ox: number,
  oy: number,
  lastTargetId: number,
  aimX: number,
  aimY: number
): number {
  const fD = range > 0 ? 1 - d / range : 0;
  switch (policy) {
    case POLICY_NEAREST:
      return 1.0 * fD;
    case POLICY_NEAREST_THREAT:
      return 0.6 * (e.threat_score / 100) + 0.4 * fD;
    case POLICY_LOWEST_HP:
      return 0.7 * (1 - e.hp / candMaxHp) + 0.3 * fD;
    case POLICY_HIGHEST_HP:
      return 0.7 * (e.hp / candMaxHp) + 0.3 * fD;
    case POLICY_DENSEST_CLUSTER:
      return 0.7 * clusterFactor(w, e.pos.x, e.pos.y) + 0.3 * fD;
    case POLICY_FORWARD_CONE: {
      const dx = wrapDeltaX(e.pos.x - ox);
      const dy = wrapDeltaY(e.pos.y - oy);
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const cos = (dx / len) * lastHeadingX + (dy / len) * lastHeadingY;
      const theta = (Math.acos(cos < -1 ? -1 : cos > 1 ? 1 : cos) * 180) / Math.PI;
      let fF = 1 - theta / FORWARD_FALLOFF_DEG;
      if (fF < 0) fF = 0;
      else if (fF > 1) fF = 1;
      return 0.6 * fF + 0.4 * fD;
    }
    case POLICY_LAST_TARGET:
      return 2.0 * (e.id === lastTargetId ? 1 : 0) + 1.0 * fD;
    case POLICY_BOSS_PRIORITY:
      return 10 * (e.is_boss ? 1 : 0) + 4 * (e.elite ? 1 : 0) + 0.4 * fD + 0.2 * (e.threat_score / 100);
    case POLICY_ELITE_PRIORITY:
      return 8 * (e.elite || e.is_boss ? 1 : 0) + 0.5 * (e.threat_score / 100) + 0.3 * fD;
    case POLICY_PLAYER_SELECTED: {
      const dx = wrapDeltaX(e.pos.x - ox);
      const dy = wrapDeltaY(e.pos.y - oy);
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const cos = (dx / len) * aimX + (dy / len) * aimY;
      const theta = (Math.acos(cos < -1 ? -1 : cos > 1 ? 1 : cos) * 180) / Math.PI;
      let fAim = 1 - theta / FOCUS_CONE_DEG;
      if (fAim < 0) fAim = 0;
      else if (fAim > 1) fAim = 1;
      return 3.0 * fAim + 2.0 * (e.is_boss ? 1 : 0) + 1.0 * (e.elite ? 1 : 0) + 0.5 * fD;
    }
    default:
      return fD;
  }
}

/** Score of the last selection, filled by the previous acquireTarget call. */
let lastCurrentScore = 0;
/** True when the last acquireTarget call found the incoming `lastTargetId` still valid. */
let lastCurrentValid = false;
/** Raw best score (ignoring hysteresis) of the last acquireTarget call. */
let lastBestScore = 0;

export function lastAcquisitionCurrentScore(): number {
  return lastCurrentScore;
}
export function lastAcquisitionBestScore(): number {
  return lastBestScore;
}

/**
 * §5.1–§5.5 — score every candidate under `policy` and return the winning entity id
 * (0 = no target). Hysteresis (§5.5) keeps `lastTargetId` unless a challenger beats it by
 * the configured margin. Ties resolve on lowest entity id (§5.9).
 *
 * `originX/originY` is the firing point, `range` the §5.8 acquisition range.
 */
export function acquireTarget(
  w: World,
  policy: string,
  originX: number,
  originY: number,
  range: number,
  lastTargetId: number
): number {
  const code = POLICY_CODES[policy] ?? POLICY_NEAREST;
  return acquireTargetByCode(w, code, originX, originY, range, lastTargetId, 1, 0);
}

/** Numeric-policy form used by the hot path; `aimX/aimY` only matter for policy 10. */
export function acquireTargetByCode(
  w: World,
  policy: number,
  originX: number,
  originY: number,
  range: number,
  lastTargetId: number,
  aimX: number,
  aimY: number
): number {
  rebuildEnemyIndex(w);
  refreshHeading(w);

  // §5.8 — the single choke point for every acquisition in the game: all ten policies, the
  // Focus-Aim overlay, the lobbed-weapon cluster picks, the chain fork, the scion volley and
  // the barb/homing re-targets all arrive here. Clamping once, here, is what makes the
  // on-screen rule exhaustive.
  //
  // The candidate filter measures visibility from the ship, so a query whose origin has
  // travelled `originOffset` units away may legitimately reach `visible + originOffset`
  // from that origin and still land inside the ring.
  const originOffset = Math.sqrt(wrapDist2(w.player.pos.x, w.player.pos.y, originX, originY));
  const bound = visibleAcquisitionRange() + originOffset;
  const effRange = range < bound ? range : bound;

  let searchRadius = effRange;
  if (policy === POLICY_LAST_TARGET) searchRadius = effRange * LAST_TARGET_RANGE_FACTOR;
  gatherCandidates(w, originX, originY, searchRadius);

  lastCurrentScore = 0;
  lastCurrentValid = false;
  lastBestScore = 0;
  if (candCount === 0) return 0;

  // §5.3 policy 6 prefilter: θ ≤ 60° of heading; falls back to the full circle when empty.
  let prefilter = policy === POLICY_FORWARD_CONE;
  if (prefilter) {
    let inCone = 0;
    for (let i = 0; i < candCount; i++) {
      const e = w.enemies.slots[candIdx[i]];
      const dx = wrapDeltaX(e.pos.x - originX);
      const dy = wrapDeltaY(e.pos.y - originY);
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      if ((dx / len) * lastHeadingX + (dy / len) * lastHeadingY >= FORWARD_PREFILTER_COS) inCone++;
    }
    if (inCone === 0) prefilter = false;
  }
  // §5.10 policy 10: candidates are cone-only; empty cone means no overlay target.
  const coneOnly = policy === POLICY_PLAYER_SELECTED;
  const coneCos = Math.cos((FOCUS_CONE_DEG * Math.PI) / 180);

  let bestId = 0;
  let bestScore = -Infinity;

  for (let i = 0; i < candCount; i++) {
    const e = w.enemies.slots[candIdx[i]];
    const d = candDist[i];
    const dx = wrapDeltaX(e.pos.x - originX);
    const dy = wrapDeltaY(e.pos.y - originY);
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    if (prefilter) {
      if ((dx / len) * lastHeadingX + (dy / len) * lastHeadingY < FORWARD_PREFILTER_COS) continue;
    }
    if (coneOnly) {
      if ((dx / len) * aimX + (dy / len) * aimY < coneCos) continue;
    }
    const s = scoreCandidate(w, e, d, effRange, policy, originX, originY, lastTargetId, aimX, aimY);
    if (e.id === lastTargetId && d <= effRange * LEASH_FACTOR) {
      lastCurrentValid = true;
      lastCurrentScore = s;
    }
    if (s > bestScore + SCORE_EPSILON) {
      bestScore = s;
      bestId = e.id;
    } else if (Math.abs(s - bestScore) <= SCORE_EPSILON && e.id < bestId) {
      bestId = e.id;
    }
  }

  if (bestId === 0) return 0;
  lastBestScore = bestScore;

  // §5.5 hysteresis: a voluntary switch needs challenger > current × (1 + m).
  if (lastCurrentValid && bestId !== lastTargetId) {
    const margin = policy === POLICY_PLAYER_SELECTED
      ? POLICY10_HYSTERESIS_MARGIN
      : cfg.get('targeting.hysteresis_margin');
    const threshold = lastCurrentScore >= 0
      ? lastCurrentScore * (1 + margin)
      : lastCurrentScore * (1 - margin);
    if (bestScore <= threshold) return lastTargetId;
  }
  return bestId;
}

// --- prediction (§5.7) ---------------------------------------------------------------------------

/**
 * §5.7 — one fixed-point iteration of projectile lead. Writes a unit aim direction into
 * `aimOut` and returns the distance to the aim point. `projectileSpeed <= 0` means an
 * instant weapon: direct aim, no lead.
 */
export function aimAt(
  shooterX: number,
  shooterY: number,
  target: Enemy,
  projectileSpeed: number
): number {
  // Locally-Euclidean frame around the shooter: the target's nearest image may sit outside
  // `[-100, 100)`, which is exactly what the lead maths below needs. Nothing here is stored as
  // a position, so no result needs re-canonicalising.
  const tx = nearestX(shooterX, target.pos.x);
  const ty = nearestY(shooterY, target.pos.y);
  let px = tx;
  let py = ty;
  if (projectileSpeed > 0) {
    const dx0 = px - shooterX;
    const dy0 = py - shooterY;
    const d0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
    const t0 = d0 / projectileSpeed;
    const p1x = px + target.vel.x * t0;
    const p1y = py + target.vel.y * t0;
    const d1 = Math.sqrt((p1x - shooterX) * (p1x - shooterX) + (p1y - shooterY) * (p1y - shooterY));
    let t1 = d1 / projectileSpeed;
    if (t1 < 0) t1 = 0;
    else if (t1 > MAX_LEAD_S) t1 = MAX_LEAD_S;
    px = tx + target.vel.x * t1;
    py = ty + target.vel.y * t1;
  }
  const dx = px - shooterX;
  const dy = py - shooterY;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d < 1e-6) {
    aimOut.x = 1;
    aimOut.y = 0;
    return 0;
  }
  aimOut.x = dx / d;
  aimOut.y = dy / d;
  return d;
}

// --- step 7 -----------------------------------------------------------------------------------

/** §5.5 — a target is invalid when dead, untargetable or beyond the leash. */
function targetInvalid(w: World, id: number, ox: number, oy: number, range: number): boolean {
  if (id === 0) return true;
  const e = enemyByEntityId(w, id);
  if (e === null || !isTargetable(e)) return true;
  const dx = wrapDeltaX(e.pos.x - ox);
  const dy = wrapDeltaY(e.pos.y - oy);
  const leash = range * LEASH_FACTOR;
  return dx * dx + dy * dy > leash * leash;
}

/** Reset the per-slot scheduling state (called on world reset / tick rewind). */
export function resetTargeting(): void {
  slotTargetSince.fill(0);
  slotNextEval.fill(0);
  slotFocusHeld.fill(0);
  lastHeadingX = 1;
  lastHeadingY = 0;
  idMapTick = -1;
  idMapWorld = null;
  for (let i = 0; i < runtimes.length; i++) {
    runtimes[i].def_id = '';
    runtimes[i].level = 0;
    runtimes[i].valid = false;
  }
  activeRuntime.def_id = '';
  activeRuntime.level = 0;
  activeRuntime.valid = false;
}

/**
 * Step 7 — per-weapon target acquisition. Staggered so at most one full evaluation lands per
 * tick across four slots (§5.4). Invalidated targets re-evaluate on the next tick with no
 * margin and no min-hold.
 */
export function stepTargeting(w: World): void {
  if (w.tick === 0) resetTargeting();
  rebuildEnemyIndex(w, true);
  refreshHeading(w);

  const p = w.player;
  const px = p.pos.x;
  const py = p.pos.y;
  const focusHeld = p.focus_aim.held;
  const focusSlot = p.focus_slot;
  let aimX = p.focus_aim.dir.x;
  let aimY = p.focus_aim.dir.y;
  const aimLen = Math.sqrt(aimX * aimX + aimY * aimY);
  if (aimLen > 1e-6) {
    aimX /= aimLen;
    aimY /= aimLen;
  } else {
    aimX = lastHeadingX;
    aimY = lastHeadingY;
  }

  for (let s = 0; s < p.weapon_count && s < 4; s++) {
    const slot = p.weapons[s];
    if (slot.def_id === '') continue;
    const rt = weaponRuntime(w, s);
    if (!rt.valid) continue;

    // §5.8 — the lock is held on the clamped range too, so a target that walks off screen
    // is dropped at 1.1× the ring instead of being tracked to the weapon's full 62 u. The
    // 10% of slack is what stops a body pacing the rim from flickering the lock.
    const range = clampToVisible(rt.range);
    const invalid = targetInvalid(w, slot.last_target_id, px, py, range);
    const isFocus = s === focusSlot && focusHeld;
    // §5.10 — releasing Focus Aim is a policy change, not a voluntary re-score: it forces an
    // immediate re-evaluation so the fallback target is restored within one cycle (≤ 100 ms).
    const releaseEdge = slotFocusHeld[s] === 1 && !isFocus;
    slotFocusHeld[s] = isFocus ? 1 : 0;
    const interval = isFocus ? FOCUS_REACQUIRE_TICKS : REACQUIRE_TICKS;
    const scheduled = (w.tick + s) % interval === 0;
    if (!invalid && !scheduled && !releaseEdge) continue;
    // §5.5 min-hold: 250 ms before a voluntary switch; invalidation, the policy-10 overlay
    // and the overlay-release edge are exempt.
    const held = !invalid && !isFocus && !releaseEdge && w.tick - slotTargetSince[s] < MIN_HOLD_TICKS;
    if (held) continue;

    const policy = effectivePolicy(w, s, rt);
    const fallbackId = acquireTargetByCode(w, policy, px, py, range, invalid ? 0 : slot.last_target_id, aimX, aimY);

    let nextId = fallbackId;
    if (isFocus) {
      const coneId = acquireTargetByCode(w, POLICY_PLAYER_SELECTED, px, py, range, slot.last_target_id, aimX, aimY);
      if (coneId !== 0) {
        // §5.10 — aiming at trash never pulls a hard-priority weapon off a boss/elite.
        const cone = enemyByEntityId(w, coneId);
        const fallback = fallbackId !== 0 ? enemyByEntityId(w, fallbackId) : null;
        const conePriority = cone !== null && (cone.is_boss || cone.elite);
        const fallbackPriority = fallback !== null && (fallback.is_boss || fallback.elite);
        nextId = !conePriority && fallbackPriority ? fallbackId : coneId;
      }
    }

    if (nextId !== slot.last_target_id) {
      slot.last_target_id = nextId;
      slotTargetSince[s] = w.tick;
    }
    slotNextEval[s] = w.tick + interval;
  }
}
